<script setup>
import { filter, isEqual, keyBy } from 'lodash';
import HawkCustomFieldValueRender from '~/common/components/molecules/hawk-custom-field-value-render/hawk-custom-field-value-render.vue';
import HawkTable from '~/common/components/organisms/hawk-table/hawk-table.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useDashboardStore } from '~/dashboard/store/dashboard.store.js';

const props = defineProps({
  data: {
    type: Object,
  },
  id: {
    type: String,
  },
  // eslint-disable-next-line vue/prop-name-casing
  is_editing: {
    type: Boolean,
    default: false,
  },
});
const { $t, common_store } = useCommonImports();
const dashboard_store = useDashboardStore();

const forceUpdate = ref(0);
const loading = ref(false);
const no_data = ref(false);

const widget_data = computed(() => props.data?.data || {});

const columns = computed(() => {
  return props.data?.data?.columns?.map(col => ({
    accessorKey: col.accessorKey,
    id: col.id,
    header: col.header,
    size: col.size || '400',
    agg: common_store.assets_custom_fields_map[col.accessorKey]?.properties?.aggregation || null,
  })) || [];
});

const assets = computed(() => {
  return common_store?.assets || [];
});

const activities = computed(() => {
  if (!assets.value?.length)
    return [];

  return assets.value.map((asset) => {
    const parsedAsset = {
      uid: asset.uid,
      name: asset.name,
      code: asset.code,
      address: asset.address?.name || '-',
      created_at: asset.created_at,
      created_by: asset.created_by,
    };

    if (asset.metadata) {
      Object.entries(asset.metadata).forEach(([field, value]) => {
        parsedAsset[field] = value;
      });
    }

    return parsedAsset;
  });
});

const height = computed(() => {
  return ((props.data.h || 22) * 20) - 44;
});

const metadata_fields = computed(() => {
  return filter(columns.value, c => !['name', 'code', 'address', 'context_menu'].includes(c.id))
    .map((item) => {
      return {
        ...item,
        ...(common_store.assets_custom_fields_map[item.accessorKey] || {}),
      };
    });
});

function footer_function(col_id) {
  if (col_id === 'name')
    return $t('Summary');

  const cf = metadata_fields.value.find(m => m.id === col_id);
  if (cf?.properties?.aggregation)
    return getTotal(activities.value, cf);
  return '-';
}

function getTotal(data, cf) {
  if (!cf.properties.aggregation || !['sum', 'min', 'max', 'avg', 'count'].includes(cf?.properties?.aggregation))
    return '';

  const values = data.map((item) => {
    if (item[cf.uid]) {
      const metadataValue = item[cf.uid];
      const value = metadataValue?.value || metadataValue;
      const numValue = typeof value === 'number' ? value : Number.parseFloat(value);
      return Number.isNaN(numValue) ? 0 : numValue;
    }
    else {
      const value = item[cf.accessorKey];
      const numValue = typeof value === 'number' ? value : Number.parseFloat(value);
      return Number.isNaN(numValue) ? 0 : numValue;
    }
  });

  const total_count = values.length;

  switch (cf.properties.aggregation) {
    case 'count': return total_count;
    case 'sum': return formatNumber(values.reduce((sum, val) => sum + val, 0));
    case 'avg': return formatNumber(values.reduce((sum, val) => sum + val, 0) / total_count);
    case 'min': return formatNumber(Math.min(...values));
    case 'max': return formatNumber(Math.max(...values));
    default: return '';
  }
}

function formatNumber(value) {
  return Number.isInteger(value) ? value : Number.parseFloat(value).toFixed(2);
}

function updatePrintMap() {
  dashboard_store.update_print_map(props.id, {
    type: widget_data.value.type,
    renderAt: `chart-container-${props?.id}`,
    renderType: 'table',
    width: '100%',
    height: '100%',
    dataFormat: 'json',
    chart_name: widget_data.value.name,
    dimensions: {
      x: props.data.x,
      y: props.data.y,
    },
    dataSource: {
      columns: columns.value,
      assets: activities.value,
      dataset: activities.value,
      is_transpose: widget_data.value.transpose === true,
      dashboard_index: props.data.i,
      is_new_pivot_chart: widget_data.value.chart === 'asset_pivot_table',
    },
  });
  dashboard_store.update_new_print_map((props.data?.data?.name || 'untitled'), {
    widget_type: 'table',
    type: props.data?.data?.type,
    dataSource: parseNewPrintData(activities.value),
  });
}

function parseNewPrintData(data) {
  const accessor_keys = columns.value.map(c => c.accessorKey);
  const parsed_data = [];
  data.forEach((d) => {
    parsed_data.push(accessor_keys.map(k => String(d[k] || '-')));
  });
  return parsed_data;
}

function columnResized(_resized_column, columns_widths) {
  dashboard_store.set_table_column_widths(props?.id, columns_widths);
}

function getConfigMap(field_uid) {
  return keyBy(common_store.assets_custom_fields_map?.[field_uid]?.config, 'uid');
}
watch(() => props.data.data, (new_props_data, old_props_data) => {
  if (!old_props_data || (!isEqual(new_props_data, old_props_data))) {
    loading.value = true;
    forceUpdate.value++;
    setTimeout(() => {
      loading.value = false;
    }, 500);
    if (props?.id !== 'preview')
      updatePrintMap();
  }
}, { immediate: true, deep: true });
</script>

<template>
  <div>
    <div v-if="$slots['header-title'] || $slots['header-actions']" class="widget-header group">
      <slot name="header-title" />
      <slot name="header-actions" />
    </div>

    <hawk-loader v-if="loading" />
    <div v-else-if="no_data || !activities?.length" class="text-sm font-semiBold w-full" :class="dashboard_store.is_mobile_view ? 'h-[240px] grid place-items-center' : 'mt-8 flex justify-center'">
      {{ $t('No data present') }}
    </div>
    <div v-else-if="activities && columns?.length && activities?.length && !loading">
      <div class="w-full scrollbar" :style="{ height: `${height}px` }">
        <HawkTable
          :key="forceUpdate"
          :data="activities"
          :columns="columns"
          freeze_column_id="name"
          :show_menu_header="false"
          :show_column_borders="true"
          additional_table_classes=""
          :container_class="`h-[${height}px]`"
          :striped="true"
          is_gapless
          :disable_resize="!dashboard_store.is_editing_dashboard"
          cell_height="30px"
          :show_footer="true"
          footer_type="column"
          :footer_function="footer_function"
          @column-resized="columnResized"
        >
          <template
            v-for="custom_field in metadata_fields"
            #[custom_field.id]="cf"
            :key="custom_field.id"
          >
            <HawkCustomFieldValueRender v-if="cf?.data?.row?.original?.[custom_field?.accessorKey] && !cf?.data?.row?.original?.isSummaryRow" :field_value="cf?.data?.row?.original?.[custom_field?.accessorKey]" field_selector="field" />
          </template>
          <template #name="uid">
            <div v-if="['null', 'undefined'].includes(uid.data.row.original.name)" class="font-semibold">
              {{ $t('Unknown') }}
            </div>
            <div v-else-if="['checkbox', 'dropdown', 'radio'].includes(common_store.assets_custom_fields_map?.[uid.data.row.original?.field_uid]?.type)">
              <div v-for="(item, index) in uid.data.getValue()?.split(',')" :key="item.id" class="inline-flex font-semibold">
                {{ getConfigMap(uid.data.row.original.field_uid)?.[item]?.name }}
                <span v-if="index !== (uid.data.getValue()?.split(',')?.length - 1)">, </span>
              </div>
            </div>
            <div v-else-if="['members'].includes(common_store.assets_custom_fields_map?.[uid.data.row.original?.field_uid]?.type)">
              <HawkMembers :members="uid.data.getValue()?.split(',')" type="badge" class="flex flex-wrap" />
            </div>
            <div
              v-else
              class="font-semibold"
              @click="!uid.data.row.original.field_uid?.length
                ? $router.push({
                  name: 'asset-dashboard',
                  params: { asset_id: uid.data.row.original.uid },
                }) : null
              "
            >
              {{ ['null', 'undefined'].includes(uid.data.row.original.name) ? 'Unknown' : uid.data.row.original.name }}
            </div>
          </template>
          <template #code="code">
            {{ code.data.row.original?.code ? `#${code.data.row.original?.code}` : '-' }}
          </template>
          <template #created_at="created_at">
            {{ $date(created_at.data.getValue(), "DATE_SHORT") }}
          </template>
          <template #created_by="created_by">
            <HawkMembers :members="created_by?.data.getValue()" type="label" :has_avatar="false" />
          </template>
        </HawkTable>
      </div>
    </div>
  </div>
</template>
